import { useApolloClient, useSubscription } from "@apollo/client/react";
import { useSchedulerState } from "hooks/state/index";
import useTimeZone from "hooks/useTimeZone";
import {
  OnDowntimeBlockCreated,
  OnDowntimeBlockDeleted,
  OnDowntimeBlocksUpdated,
  OnDowntimeBlockUpdated,
} from "./downtimeBlock/downtimeBlock.gql";
import { insertDowntimeBlock, removeDowntimeBlock } from "./downtimeBlock/helpers/handleUpdateDowntimeBlocks";
import { insertWorkBlock, removeWorkBlock } from "./workBlock/helpers/handleUpdateWorkBlocks";
import {
  OnWorkBlockCreated,
  OnWorkBlockDeleted,
  OnWorkBlocksUpdated,
  OnWorkBlockUpdated,
} from "./workBlock/workBlock.gql";
import { insertWorkOrder, removeWorkOrder } from "./workOrder/helpers/handleUpdateWorkOrders";
import useGetWorkOrderFiltered from "./workOrder/useGetWorkOrderFiltered";
import { OnWorkOrderCreated, OnWorkOrderDeleted, OnWorkOrderUpdated } from "./workOrder/workOrder.gql";

export default function useSubscriptions() {
  const client = useApolloClient();
  const { getWorkOrderFiltered, setUpdatedWorkOrder } = useGetWorkOrderFiltered();
  const {
    state: { dataTimeRange },
  } = useSchedulerState();
  const { overrideToSiteTimeZone } = useTimeZone();

  const addDowntimeToCache = ({ data: { data } }) => {
    if (!data || !data.downtimeBlockCreated) return;

    insertDowntimeBlock(data.downtimeBlockCreated.downtimeBlock, client);
  };

  const removeDowntimeFromCache = ({ data: { data } }) => {
    if (!data || !data.downtimeBlockDeleted) return;

    removeDowntimeBlock(data.downtimeBlockDeleted.downtimeBlock.id, client);
  };

  const addWorkBlockToCache = ({ data: { data } }) => {
    if (!data || !data.workBlockCreated) return;

    const getAllBlocksVariables = {
      endsAt: overrideToSiteTimeZone(dataTimeRange.endDate),
      startsAt: overrideToSiteTimeZone(dataTimeRange.startDate),
    };

    insertWorkBlock(data.workBlockCreated.workBlock, client, getAllBlocksVariables);
  };

  const removeWorkBlockFromCache = ({ data: { data } }) => {
    if (!data || !data.workBlockDeleted) return;

    const getAllBlocksVariables = {
      endsAt: overrideToSiteTimeZone(dataTimeRange.endDate),
      startsAt: overrideToSiteTimeZone(dataTimeRange.startDate),
    };

    removeWorkBlock(data.workBlockDeleted.workBlock.id, client, getAllBlocksVariables);
  };

  const addWorkOrderToCache = ({ data: { data } }) => {
    if (!data || !data.workOrderCreated) return;

    insertWorkOrder(data.workOrderCreated.workOrder, client);
  };

  const removeWorkOrderFromCache = ({ data: { data } }) => {
    if (!data || !data.workOrderDeleted) return;

    removeWorkOrder(data.workOrderDeleted.workOrder.id, client);
  };

  const updateFilteredWorkOrders = ({ data: { data } }) => {
    if (!data || !data.workOrderUpdated || !data.workOrderUpdated.workOrder) return;

    setUpdatedWorkOrder(data.workOrderUpdated.workOrder);
    getWorkOrderFiltered(data.workOrderUpdated.workOrder);
  };

  useSubscription(OnDowntimeBlockCreated, { onData: addDowntimeToCache });
  useSubscription(OnDowntimeBlockDeleted, { onData: removeDowntimeFromCache });
  useSubscription(OnDowntimeBlockUpdated);
  useSubscription(OnDowntimeBlocksUpdated);
  useSubscription(OnWorkBlockCreated, { onData: addWorkBlockToCache });
  useSubscription(OnWorkBlockDeleted, { onData: removeWorkBlockFromCache });
  useSubscription(OnWorkBlockUpdated);
  useSubscription(OnWorkBlocksUpdated);
  useSubscription(OnWorkOrderCreated, { onData: addWorkOrderToCache });
  useSubscription(OnWorkOrderDeleted, { onData: removeWorkOrderFromCache });
  useSubscription(OnWorkOrderUpdated, { onData: updateFilteredWorkOrders });
}
